import { createDefaultHeaderRow, createDefaultPlanTableData } from './defaults'
import type { TableData, TableRow } from './types'

const HEADER_ROW_KEY = '__header__'

const escapeClipboardCell = (value: string): string => {
  if (/[\t\r\n"]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const parseTsvLine = (line: string): string[] => {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]

    if (inQuotes) {
      if (char === '"') {
        if (line[index + 1] === '"') {
          current += '"'
          index += 1
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
      continue
    }

    if (char === '"') {
      inQuotes = true
    } else if (char === '\t') {
      values.push(current)
      current = ''
    } else {
      current += char
    }
  }

  values.push(current)
  return values
}

const parseClipboardRows = (text: string): string[][] => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = normalized.split('\n')

  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop()
  }

  return lines.map(parseTsvLine)
}

const getCellValue = (row: TableRow, columnKey: string): string => {
  const cell = row.cells.find(entry => entry.columnKey === columnKey)
  return cell?.value[0] ?? ''
}

/** Serializes plan table data as tab-separated text for Excel clipboard exchange. */
export const tableDataToClipboardText = (table: TableData): string => {
  const headerRow =
    table.headerRow ?? createDefaultHeaderRow(table.columns.map(column => column.key))

  const lines = [
    table.columns
      .map(column => escapeClipboardCell(getCellValue(headerRow, column.key)))
      .join('\t'),
    ...table.rows.map(row =>
      table.columns.map(column => escapeClipboardCell(getCellValue(row, column.key))).join('\t'),
    ),
  ]

  return lines.join('\n')
}

const buildTableRow = (key: string, columnKeys: string[], values: string[]): TableRow => ({
  key,
  cells: columnKeys.map((columnKey, index) => ({
    columnKey,
    value: [values[index] ?? ''],
  })),
})

/** Parses tab-separated clipboard text (e.g. from Excel) into plan table data. */
export const clipboardTextToTableData = (
  text: string,
  settings: TableData['settings'] = createDefaultPlanTableData().settings,
): TableData => {
  const parsedRows = parseClipboardRows(text.trim())
  if (parsedRows.length === 0) {
    throw new Error('Clipboard text is empty')
  }

  const columnCount = Math.max(...parsedRows.map(row => row.length), 1)
  const columnKeys = Array.from({ length: columnCount }, (_, index) => `col-${index + 1}`)
  const columns = columnKeys.map(key => ({ key }))
  const [headerValues, ...dataValues] = parsedRows

  return {
    columns,
    headerRow: buildTableRow(HEADER_ROW_KEY, columnKeys, headerValues),
    rows: dataValues.map((values, index) => buildTableRow(`row-${index + 1}`, columnKeys, values)),
    settings,
  }
}
